<template>
    <div class="min-h-screen bg-white flex">
        <!-- Left panel: black with logo -->
        <div class="hidden lg:flex lg:w-1/2 bg-[#17364a] flex-col items-center justify-center p-16 relative overflow-hidden">
            <!-- Subtle gold diagonal stripe -->
            <div class="absolute inset-0 opacity-5" style="background: repeating-linear-gradient(45deg, #A07830, #A07830 1px, transparent 1px, transparent 40px)"></div>
            <div class="relative z-10 flex flex-col items-center text-center">
                <img src="/images/logo.png" alt="Nobel Academy" class="h-16 w-auto brightness-0 invert mb-10" />
                <h2 class="text-3xl font-extrabold text-white leading-tight mb-4">
                    Your Journey to<br /><span class="text-[#A07830]">Lifelong Learning</span><br />Starts Here.
                </h2>
                <p class="text-gray-500 text-sm leading-relaxed max-w-sm">
                    Join over 1 million students learning from expert-certified tutors across a wide range of subjects.
                </p>
                <!-- Quote -->
                <div class="mt-12 border-l-2 border-[#A07830] pl-5 text-left max-w-sm">
                    <p class="text-sm text-gray-400 italic">"Nobel Academy transformed the way I learn. Best investment I've ever made."</p>
                    <p class="mt-3 text-xs text-[#A07830] font-semibold">— Sarah M., Student</p>
                </div>
            </div>
        </div>

        <!-- Right panel: white form -->
        <div class="flex-1 flex flex-col items-center justify-center px-6 py-16 lg:px-16">
            <div class="w-full max-w-sm">
                <!-- Mobile logo -->
                <img src="/images/logo.png" alt="Nobel Academy" class="h-10 w-auto mb-8 lg:hidden" />

                <h1 class="text-2xl font-extrabold text-[#17364a] mb-2">Sign in</h1>
                <p class="text-sm text-gray-500 mb-8">Welcome back. Enter your credentials to continue.</p>

                <form class="flex flex-col gap-5" @submit.prevent="submitForm">
                    <!-- Error message -->
                    <div v-if="errors?.email?.length || errors?.password?.length || errors?.general?.length"
                        class="px-4 py-3 border-l-2 border-[#A07830] bg-[#A07830]/5 text-sm text-[#A07830]">
                        {{ errors.email?.[0] || errors.password?.[0] || errors.general?.[0] }}
                    </div>

                    <div>
                        <label for="email" class="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Email</label>
                        <FloatLabel variant="in" class="w-full">
                            <InputText :invalid="!!errors.email" id="email" v-model="email" variant="filled" class="w-full" type="email" />
                            <label for="email">Email</label>
                        </FloatLabel>
                    </div>

                    <div>
                        <label for="password" class="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Password</label>
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="password" v-model="password" variant="filled" class="w-full" type="password" />
                            <label for="password">Password</label>
                        </FloatLabel>
                        <div class="mt-2 text-right">
                            <Button label="Forgot password?" variant="link" class="text-xs text-[#A07830] p-0" />
                        </div>
                    </div>

                    <Button
                        class="w-full mt-2"
                        type="submit"
                        :loading="loading"
                        label="Sign in"
                        style="background: #17364a; border: none; border-radius: 0; font-weight: 600;"
                    />
                </form>

                <p class="mt-8 text-center text-sm text-gray-500">
                    Don't have an account?
                    <a href="/register" class="font-semibold text-[#A07830] hover:underline ml-1">Sign up</a>
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import Button from 'primevue/button';
import { ref } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { useRouter } from 'vue-router';

const email = ref('');
const password = ref('');
const loading = ref(false);
const errors = ref({});
const route = useRouter();

const submitForm = () => {
    loading.value = true;

    useAuth()
        .login(email.value, password.value)
        .then(() => {
            route.push({ name: 'home' });
        })
        .catch((error) => {
            if (error.response?.status === 422) {
                errors.value = error.response.data.errors;
                return;
            }
            errors.value = { general: [error.response?.data?.message] } ?? { general: ['An error occurred during login.'] };
        })
        .finally(() => {
            loading.value = false;
        });
};
</script>
